import React, { useState, useMemo, useEffect } from 'react';
import { usePatchForm } from '../../providers/PatchFormProvider';
import Select from 'react-select';

const Sandbox = () => {
  const { patchFormData, setPatchFormData } = usePatchForm();
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [radioValue, setRadioValue] = useState('progress');



  const isGroup = patchFormData.deviceSelection?.selectedType === 'Group';

  // Get all selected devices
  const selectedDevices = useMemo(() => {
    if (!patchFormData.deviceSelection) return [];
    return isGroup
      ? patchFormData.deviceSelection?.agentsData?.filter((agent) =>
        patchFormData.deviceSelection.selectedAgentIds.includes(agent.tenantAgentId),
      ) || []
      : patchFormData.deviceSelection?.osAgentsData?.filter((agent) =>
        patchFormData.deviceSelection.selectedDeviceIds.includes(agent.agentId),
      ) || [];
  }, [isGroup, patchFormData.deviceSelection]);

  // Create device options for select dropdown
  const deviceOptions = selectedDevices.map((device) => ({
    value: isGroup ? device.tenantAgentId : device.agentId,
    label: device.deviceName,
  }));

  const isSandboxDisabled = deviceOptions.length < 2;

  // Update selected device in patchFormData as a list
  const handleDeviceChange = (option) => {
    setSelectedDevice(option ? option.value : '');
    setPatchFormData((prev) => ({
      ...prev,
      RemediationPlanSandboxDevice: option ? [{ TenantAgentID: option.value }] : [],
    }));
  };

  // Update radio value in patchFormData as RemediationPlanSandboxProgressStatus
  const handleRadioChange = (value) => {
    setRadioValue(value);
    setPatchFormData((prev) => ({
      ...prev,
      RemediationPlanSandboxProgressStatus: value === 'progress' ? 'ProgressOnSuccess' : 'ProgressOnApproval',
    }));
  };
  // Set default radio value in context on mount
  useEffect(() => {
    if (isSandboxDisabled) {
      setPatchFormData((prev) => ({
        ...prev,
        RemediationPlanSandboxProgressStatus: null,
        RemediationPlanSandboxDevice: [],
      }));
    } else {
      setPatchFormData((prev) => ({
        ...prev,
        RemediationPlanSandboxProgressStatus: 'ProgressOnSuccess',
      }));
    }
  }, [isSandboxDisabled, setPatchFormData]);
  return (
    <div className="bg-[#111] text-white p-6 ">
      <div className="space-y-6">
        {/* Device Selection */}
        <div className="space-y-2">
          <label htmlFor="device-select" className="block text-sm font-medium text-gray-300">
            Select Device
          </label>
          <Select
            id="device-select"
            value={deviceOptions.find((option) => option.value === selectedDevice)}
            onChange={handleDeviceChange}
            options={deviceOptions}
            placeholder="Search or select a device..."
            isClearable
            isDisabled={isSandboxDisabled}
            styles={{
              control: (base, state) => ({
                ...base,
                backgroundColor: 'transparent',
                color: 'white',
                borderColor: state.isFocused ? '#ef4444' : '#4B5563', // red-500 on focus
                boxShadow: state.isFocused ? '0 0 0 1.5px #ef4444' : 'none', // red-500 shadow on focus
                '&:hover': {
                  borderColor: '#ef4444',
                },
              }),
              singleValue: (base) => ({
                ...base,
                color: 'white',
              }),
              input: (base) => ({
                ...base,
                color: 'white',
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: '#222',
                color: 'white',
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused ? '#333' : '#222',
                color: 'white',
              }),
              placeholder: (base) => ({
                ...base,
                color: '#aaa',
              }),
            }}
          />
        </div>

        {/* Radio Button Options */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-200">Configuration Options</h3>
          <div
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => !isSandboxDisabled && handleRadioChange('progress')}
            style={{ opacity: isSandboxDisabled ? 0.5 : 1, pointerEvents: isSandboxDisabled ? 'none' : 'auto' }}
          >
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${radioValue === 'progress' ? 'border-red-500 bg-red-500' : 'border-gray-600 bg-transparent'
                }`}
            >
              {radioValue === 'progress' && <div className="w-2 h-2 rounded-full bg-black"></div>}
            </div>
            <label className="text-sm font-medium text-gray-300 cursor-pointer">Progress on Success</label>
          </div>
          <div
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => !isSandboxDisabled && handleRadioChange('approval')}
            style={{ opacity: isSandboxDisabled ? 0.5 : 1, pointerEvents: isSandboxDisabled ? 'none' : 'auto' }}
          >
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${radioValue === 'approval' ? 'border-red-500 bg-red-500' : 'border-gray-600 bg-transparent'
                }`}
            >
              {radioValue === 'approval' && <div className="w-2 h-2 rounded-full bg-black"></div>}
            </div>
            <label className="text-sm font-medium text-gray-300 cursor-pointer">Progress on Approval</label>
          </div>
        </div>
      </div>
      {isSandboxDisabled && (
        <div className="mt-4 text-sm text-red-400">
          At least two devices are required to enable the sandbox feature.
        </div>
      )}
    </div>
  );
};

export default Sandbox;
